<script setup lang="ts">
import { computed } from 'vue';

import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import { formatPhoneNumber } from '@/utils/strings/phone-number.util';
import PhoneCallIcon from '@/assets/svg/phone-call-01-20.svg?component';
import MailIcon from '@/assets/svg/mail-01-20.svg?component';
import MarkerPinIcon from '@/assets/svg/marker-pin-20.svg?component';

const proProfileService = new ProProfileService();

// The conversation selection loads the pro profile before navigation, so the
// contact tab reads the same payload that already carries phone/email/location.
const profile = computed(() => proProfileService.selectedProProfile);

const location = computed(() => {
  const city = profile.value?.city;
  const state = profile.value?.state;
  if (city && state) {
    return `${city}, ${state}`;
  }
  return profile.value?.pro?.postalCode ?? '';
});

const rows = computed(() =>
  [
    {
      key: 'phone',
      icon: PhoneCallIcon,
      label: 'Phone',
      // formatPhoneNumber returns '' for anything that isn't exactly 10 digits
      // (e.g. a country-code-prefixed number); fall back to the raw value so the
      // phone row is never silently dropped.
      value: profile.value?.phone
        ? formatPhoneNumber(profile.value.phone) || profile.value.phone
        : '',
    },
    { key: 'email', icon: MailIcon, label: 'Email', value: profile.value?.email ?? '' },
    { key: 'location', icon: MarkerPinIcon, label: 'Location', value: location.value },
  ].filter((row) => row.value),
);
</script>

<template>
  <div class="flex flex-col overflow-y-auto bg-tint-40 p-3" data-test="mobile-contact">
    <div
      v-if="profile"
      class="flex flex-col gap-3 rounded-bl-xl rounded-br rounded-tl-xl rounded-tr-xl bg-tint-0 p-4"
    >
      <div
        v-for="row in rows"
        :key="row.key"
        class="flex items-center gap-3"
        :data-test="`mobile-contact-${row.key}`"
      >
        <component :is="row.icon" aria-hidden="true" class="h-5 w-5 shrink-0 text-shade-800" />
        <div class="min-w-0">
          <p class="text-sm leading-[21px] text-shade-800">{{ row.label }}</p>
          <p class="sensitive break-words text-sm leading-[21px] text-shade-900">{{ row.value }}</p>
        </div>
      </div>
    </div>

    <v-skeleton-loader v-else boilerplate type="list-item-avatar-two-line@3" />
  </div>
</template>
